import Fuse from 'fuse.js';
import { keyBy, reduce, uniq } from 'lodash/fp';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import {
  AssignmentDistributionDatum,
  CaseAnalyticsAssignmentDistribution,
} from 'dashboard/models/case-analytics';
import { CaseTemplateBasicsOptionState } from 'dashboard/models/case-template';
import { User } from 'dashboard/models/user';
import CaseAssignmentDistributionTable from 'dashboard/views/DashboardCaseAnalyticsProductivityView/CaseAssignmentDistributionTable';
import Card from 'design-system/components/Card';

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
`;

export interface CaseAssignmentDistributionCardProps extends CaseAnalyticsAssignmentDistribution {
  users: User[];
  allStates: CaseTemplateBasicsOptionState[];
  loading: boolean;
}

const CaseAssignmentDistributionCard = ({
  users,
  allStates,
  data,
  loading,
}: CaseAssignmentDistributionCardProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const stateMap = keyBy((x) => x.token.toLowerCase(), allStates);

  const allDataStateIds = uniq(
    data.flatMap((datum: AssignmentDistributionDatum) =>
      Object.keys(datum).filter((key) => key.startsWith('state_')),
    ),
  );
  const dataWithUserNamesAndStateNames = useMemo(() => {
    if (loading) {
      return [];
    }

    const stateIdsByName = reduce(
      (result, id) => {
        // Fallback to unknown states when state id is not matching
        const stateName = stateMap[id]?.name || 'Unknown state';
        if (Object.prototype.hasOwnProperty.call(result, stateName)) {
          result[stateName].push(id);
        } else {
          result[stateName] = [id];
        }
        return result;
      },
      {} as Record<string, string[]>,
      allDataStateIds,
    );

    return data
      .map((datum) => {
        const assignee = users.find((user) => user.id === datum.assignee_token);

        const statesDataWithName = Object.fromEntries(
          Object.keys(stateIdsByName).map((stateName) => {
            const statesCount = stateIdsByName[stateName].reduce((sum, stateId) => {
              return sum + (datum[stateId] as number);
            }, 0);
            return [stateName, statesCount];
          }),
        );
        return {
          assignee_display_name: userDisplayName(assignee),
          assignee_token: datum.assignee_token,
          total_cases: datum.total_cases,
          ...statesDataWithName,
        };
      })
      .filter((datum) => {
        // filtering out other users' analytics when current user does not have users.view permissions
        return datum.assignee_display_name != null;
      });
  }, [data, users, loading]);

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [{ name: 'assignee_display_name', weight: 0.8 }],
    };

    return new Fuse(dataWithUserNamesAndStateNames, fuseOptions);
  }, [dataWithUserNamesAndStateNames]);

  const displayData =
    searchTerm === ''
      ? dataWithUserNamesAndStateNames
      : fuse.search(searchTerm).map(({ item }) => item);

  return (
    <Card>
      <Card.Body>
        <HeaderContainer>
          <Card.Title id='case-analytics-productivity__case-assignment-card--title'>
            Assignment distribution
          </Card.Title>
          <SearchInput
            title={null}
            value={searchTerm}
            disabled={loading}
            handleInput={setSearchTerm}
            placeholder='Search by name'
            dataTest='case-analytics__assignment-distribution--search'
          />
        </HeaderContainer>
        {loading ? (
          <LoadingIndicator />
        ) : (
          <CaseAssignmentDistributionTable
            data={displayData as AssignmentDistributionDatum[]}
            isLoading={loading}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default CaseAssignmentDistributionCard;
